import React, { useState, useRef, useEffect, useCallback } from 'react';

export type ResizeDirection = 'top' | 'right' | 'bottom' | 'left';

export function useResizeHelpers(id: string, disabled = false) {
  const [dimensions, setDimensions] = useState(() => {
    const el = document.getElementById(id);

    const rect = el?.getBoundingClientRect();
    return {
      width: rect?.width || 500,
      height: rect?.height || 601,
      top: rect?.top || 0,
      left: rect?.left || 0,
    };
  });

  const [isResizing, setIsResizing] = useState(false);
  const resizeDirection = useRef<ResizeDirection | null>(null);
  const startPosition = useRef({ x: 0, y: 0 });

  const startResize = (direction: ResizeDirection, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    resizeDirection.current = direction;
    startPosition.current = { x: event.clientX, y: event.clientY };
    setIsResizing(true);
  };

  const handleResize = (event: MouseEvent) => {
    if (!resizeDirection.current) return;

    const deltaX = event.clientX - startPosition.current.x;
    const deltaY = event.clientY - startPosition.current.y;

    setDimensions((prev) => {
      let newWidth = prev.width;
      let newHeight = prev.height;
      let newTop = prev.top;
      let newLeft = prev.left;

      if (resizeDirection.current === 'right') {
        newWidth = Math.max(50, prev.width + deltaX);
      }
      if (resizeDirection.current === 'bottom') {
        newHeight = Math.max(50, prev.height + deltaY);
      }
      if (resizeDirection.current === 'left') {
        const newCalculatedWidth = Math.max(50, prev.width - deltaX);
        if (newCalculatedWidth > 400) {
          newLeft = prev.left + (prev.width - newCalculatedWidth); // Move left boundary
          newWidth = newCalculatedWidth;
        }
      }
      if (resizeDirection.current === 'top') {
        const newCalculatedHeight = Math.max(50, prev.height - deltaY);
        if (newCalculatedHeight > 600) {
          newTop = prev.top + (prev.height - newCalculatedHeight); // Move top boundary
          newHeight = newCalculatedHeight;
        }
      }

      return { width: newWidth, height: newHeight, top: newTop, left: newLeft };
    });

    startPosition.current = { x: event.clientX, y: event.clientY };
  };

  const stopResize = useCallback(() => {
    setIsResizing(false);
    resizeDirection.current = null;
  }, []);

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleResize);
      document.addEventListener('mouseup', stopResize);
    } else {
      document.removeEventListener('mousemove', handleResize);
      document.removeEventListener('mouseup', stopResize);
    }

    return () => {
      document.removeEventListener('mousemove', handleResize);
      document.removeEventListener('mouseup', stopResize);
    };
  }, [isResizing, stopResize]);

  return { dimensions, isResizing, startResize, setDimensions };
}
